import moment from 'moment';
import * as Yup from 'yup';
import { isEmail } from 'validator';

import { correctlyFormatDate } from 'utils/dates';

export const isValidEmail = (email) => {
  const schema = Yup.string().min(6).max(50).email();

  return schema.isValidSync(email) && isEmail(email);
};

export const formats = ['DD/MM/YY', 'DDMMYY', 'DD/MM/YYYY', 'DDMMYYYY', 'YYYY-MM-DD'];

export const BOOKING_PHONE_NUMBER_MAX_LENGTH = 15;
export const BOOKING_PHONE_NUMBER_PREFIX = '00';
const BOOKING_PHONE_NUMBER_MIN_LENGTH = 6;

export const isEmpty = (value) => !value;

export const nameRegex = /^[A-Za-z' ÀÁÂÃÄÅÆàáâãäåæÇçÈÉÊËèéêëÌÍÎÏìíîïÐðÑñÒÓÔÕÖØòóôõöøÙÚÛÜùúûüÝÞýÿþ-]{2,34}$/g;

export const isValidName = (value) => !!value.trim().match(nameRegex);

export const alphanumericNameRegex = /^[A-Za-z0-9' ÀÁÂÃÄÅÆàáâãäåæÇçÈÉÊËèéêëÌÍÎÏìíîïÐðÑñÒÓÔÕÖØòóôõöøÙÚÛÜùúûüÝÞýÿþ-]{2,34}$/g;

export const groupNameRegex = alphanumericNameRegex;

export const isValidGroupName = (value) => !!value.trim().match(groupNameRegex);

export const isValidAlphanumericName = (value) => !!value.trim().match(alphanumericNameRegex);

export const isValidDate = (value) =>
  !value ||
  (moment(value, formats, true).isValid() &&
    moment(correctlyFormatDate(value)) < moment() &&
    moment(correctlyFormatDate(value)) > moment('1899-12-31'));

const normalizePhoneValue = (value) => `${value ?? ''}`;

const normalizePhoneDigits = (value) => normalizePhoneValue(value).replace(/^0+/, '');

const getMaxPhoneNumberLength = (countryCode = '', prefix = BOOKING_PHONE_NUMBER_PREFIX) =>
  Math.max(0, BOOKING_PHONE_NUMBER_MAX_LENGTH - prefix.length - normalizePhoneDigits(countryCode).length);

export const isValidCountryCode = (value) => /^[0-9]{1,4}$/g.test(normalizePhoneDigits(value));

const isValidLocalPhoneDigits = (value) => /^[0-9]{6,20}$/.test(normalizePhoneDigits(value));

export const isValidPhoneNumberWithCountryCode = (phoneNumber, countryCode, prefix = BOOKING_PHONE_NUMBER_PREFIX) =>
  isValidCountryCode(countryCode) &&
  isValidLocalPhoneDigits(phoneNumber) &&
  normalizePhoneDigits(phoneNumber).length <= getMaxPhoneNumberLength(countryCode, prefix);

export const isValidPhoneNumberWithPrefix = (phoneNumber, prefix = '') => {
  const digits = normalizePhoneValue(phoneNumber);

  return new RegExp(`^\\d{${BOOKING_PHONE_NUMBER_MIN_LENGTH},}$`).test(digits) && digits.length <= getMaxPhoneNumberLength('', prefix);
};

export const isValidInternationalPhoneNumber = (phoneNumber) => {
  const value = normalizePhoneValue(phoneNumber);
  const digits = value.startsWith('+') ? value.slice(1) : value;

  return new RegExp(`^\\d{${BOOKING_PHONE_NUMBER_MIN_LENGTH},${BOOKING_PHONE_NUMBER_MAX_LENGTH}}$`).test(digits);
};

export const isValidLoyaltyNumber = (value) => !value || /^\d{8}$/.test(value);

export const isValidPassengersNumber = (value) => !value || /^\d{1,2}$/.test(value);

export const isValidMembershipNumber = (value) => !value || /^\d{17}$/.test(value);

export const voucherReferenceReg = /^[a-zA-Z0-9]{6}$|^[a-zA-Z0-9]{7}$|^\d{9}$|^[a-zA-Z0-9]{16}$/;
export const discountCodeReferenceReg = /^[a-zA-Z0-9!@#$%^&*)(+=._-]{3,30}$/;

export const redeemVoucherReferenceReg = /^\d{9}$|^[a-zA-Z0-9]{6}$|^[a-zA-Z0-9]{16}$/;

export const socialMediaReferenceReg = /^[a-zA-Z0-9]{16}$/;

export const isValidVoucherReference = (value) => voucherReferenceReg.test(value.toString());

export const isValidRedeemVoucherReference = (value) => redeemVoucherReferenceReg.test(value.toString());

export const isValidSocialMediaReference = (value) => socialMediaReferenceReg.test(value.toString());

export const emailReg = /\S+@\S+\.\S+/;

export const pnrRegex = /^([a-zA-Z0-9]{6}|[a-zA-Z0-9]{8}|[a-zA-Z0-9]{9})$/;

export const bookingPathRegex = /^\/booking\/[a-zA-Z0-9]{6}\/.*/;

export const jacTravelReferenceRegex = /^\d{9,10}$/;

export const hotelIdRegex = /^\d{1,6}$/;

export const ticketNumberRegex = /^\d{9}$/;

export const isValidNumber = (rawValue, max = Number.MAX_SAFE_INTEGER, min = 0) => {
  if (isNaN(rawValue)) {
    return false;
  }

  const value = parseInt(rawValue);
  return value >= min && value <= max;
};
